import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PencilLine, Clock, Loader2, X, Send, ChevronDown, ChevronUp } from "lucide-react";

// F4 — the raw transcript is always preserved. When an approved correction exists
// (display_text), it is shown as the memo text and the original stays here, collapsed.
export function OriginalTranscript({ transcript, id }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-3" data-testid={`origineel-wrap-${id}`}>
      <button type="button" onClick={() => setOpen((v) => !v)} data-testid={`origineel-toggle-${id}`}
        className="tw-press inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-tw-muted hover:text-tw-yellow">
        {open ? <>Origineel transcript <ChevronUp size={12} /></> : <>Origineel transcript <ChevronDown size={12} /></>}
      </button>
      {open && (
        <p className="mt-2 pl-3 border-l-2 border-tw text-tw-muted text-sm leading-relaxed whitespace-pre-wrap" data-testid={`origineel-text-${id}`}>
          {transcript}
        </p>
      )}
    </div>
  );
}

// F4 — propose an improved memo text. The RAW transcript is never touched; the proposal
// waits for admin approval. Available to anyone who can SEE the memo (incl. lezers).
export function ProposeEdit({ recordingId, currentText }) {
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState(null); // the caller's open proposal or null
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/recordings/${recordingId}/proposals/mine`);
      setPending(data.proposal);
    } catch { /* fail-soft */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [recordingId]);

  const startEditor = () => {
    setText((pending?.proposed_text ?? currentText ?? "").toString());
    setOpen(true);
  };

  const submit = async () => {
    if (!text.trim()) { toast.error("Voorstel mag niet leeg zijn."); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/recordings/${recordingId}/proposals`, { proposed_text: text });
      setPending(data);
      setOpen(false);
      toast.success("Voorstel verstuurd — wacht op goedkeuring door een beheerder.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  const withdraw = async () => {
    if (!window.confirm("Je voorstel intrekken?")) return;
    setSaving(true);
    try {
      await api.delete(`/recordings/${recordingId}/proposals/mine`);
      setPending(null);
      setOpen(false);
      toast.success("Voorstel ingetrokken.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  if (loading) return null;

  return (
    <div className="mt-4 pt-3 border-t border-tw" data-testid={`propose-edit-${recordingId}`}>
      {open ? (
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="font-mono text-[9px] tracking-[0.3em] text-tw-yellow uppercase">Verbetering voorstellen</div>
            <button type="button" onClick={() => setOpen(false)} data-testid={`propose-close-${recordingId}`} className="tw-press text-tw-muted hover:text-white">
              <X size={15} strokeWidth={2.5} />
            </button>
          </div>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={6}
            maxLength={20000}
            data-testid={`propose-textarea-${recordingId}`}
            className="w-full bg-tw-black border border-tw text-white text-sm p-3 rounded-none focus:outline-none focus:border-tw-yellow whitespace-pre-wrap resize-y"
          />
          <p className="mt-2 text-[11px] text-tw-muted leading-relaxed">
            Je voorstel wordt pas zichtbaar na goedkeuring door een beheerder; het origineel blijft altijd bewaard.
          </p>
          <div className="mt-2 flex gap-2">
            <button type="button" onClick={submit} disabled={saving} data-testid={`propose-submit-${recordingId}`}
              className="tw-press inline-flex items-center gap-1.5 px-3 h-9 bg-tw-yellow text-black font-bold uppercase tracking-[0.15em] text-[10px] disabled:opacity-50">
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} strokeWidth={2.8} />} Versturen
            </button>
          </div>
        </div>
      ) : pending ? (
        <div className="flex items-center gap-2 flex-wrap" data-testid={`propose-pending-${recordingId}`}>
          <span className="inline-flex items-center gap-1.5 px-2 py-1 border border-tw-yellow/60 text-tw-yellow font-mono text-[10px] uppercase tracking-[0.15em]">
            <Clock size={11} strokeWidth={2.5} /> Wacht op goedkeuring
          </span>
          <button type="button" onClick={startEditor} data-testid={`propose-edit-open-${recordingId}`}
            className="tw-press font-mono text-[10px] uppercase tracking-[0.15em] text-tw-muted hover:text-tw-yellow px-2 py-1 border border-tw">
            Bewerken
          </button>
          <button type="button" onClick={withdraw} disabled={saving} data-testid={`propose-withdraw-${recordingId}`}
            className="tw-press font-mono text-[10px] uppercase tracking-[0.15em] text-tw-muted hover:text-tw-red px-2 py-1 border border-tw disabled:opacity-50">
            Intrekken
          </button>
        </div>
      ) : (
        <button type="button" onClick={startEditor} data-testid={`propose-open-${recordingId}`}
          className="tw-press inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-tw-muted hover:text-tw-yellow">
          <PencilLine size={13} strokeWidth={2.5} /> Stel verbetering voor
        </button>
      )}
    </div>
  );
}
